/**
 * Extended User Profile Interface
 * Stored in Firestore 'users' collection
 */
export interface UserProfile {
  // Basic Info
  username?: string;
  email?: string;
  displayName?: string;
  bio?: string;
  photoURL?: string;
  hasCustomPhoto?: boolean; // True if user uploaded their own photo (don't sync from Google)
  createdAt: string;

  // Email Verification
  emailVerified: boolean;
  verificationCode?: string;
  verificationCodeExpiry?: string;
  pendingDeletionAt?: string; // Account will be deleted if not verified by this time

  // Security
  backupEmail?: string;
  backupEmailVerified?: boolean;
  googleDisconnectToken?: string;
  googleDisconnectTokenExpiry?: string;

  // Social
  discordUsername?: string;
  discordId?: string;
  discordLinked?: boolean;

  // Privacy Settings
  profileVisibility: 'public' | 'private' | 'friends';
  showEmail: boolean;
  showWatchlistStats: boolean;
  showRecentActivity: boolean;
  showRatings: boolean;

  // Display Preferences
  theme: 'dark' | 'darker' | 'oled';
  posterSize: 'compact' | 'comfortable' | 'spacious';
  defaultView: 'grid' | 'list';
  language: string;

  // Notification Settings
  emailNotifications: {
    newSeasons: boolean;
    newEpisodes: boolean;
    weeklyDigest: boolean;
    recommendations: boolean;
    community: boolean;
  };

  // Social Features (Future)
  allowFriendRequests: boolean;
  shareWatchlistUpdates: boolean;
  acceptRecommendations: boolean;

  // Auto-update settings
  autoCheckFrequency: number; // minutes
  autoCheckEnabled: boolean;

  // Account Deletion (30-day grace period)
  scheduledForDeletion?: boolean;
  deletionDate?: string; // ISO date string
  deletionRequestedAt?: string; // ISO date string

  // Computed Stats (optional, can be calculated on demand)
  totalWatched?: number;
  totalHoursWatched?: number;
  longestStreak?: number;
  currentStreak?: number;
}

/**
 * Default profile settings for new users
 */
export const DEFAULT_PROFILE_SETTINGS: Partial<UserProfile> = {
  emailVerified: false,
  backupEmailVerified: false,

  profileVisibility: 'private',
  showEmail: false,
  showWatchlistStats: true,
  showRecentActivity: true,
  showRatings: true,

  theme: 'dark',
  posterSize: 'comfortable',
  defaultView: 'grid',
  language: 'en',

  emailNotifications: {
    newSeasons: true,
    newEpisodes: true,
    weeklyDigest: false,
    recommendations: true,
    community: true,
  },

  allowFriendRequests: true,
  shareWatchlistUpdates: false,
  acceptRecommendations: true,

  autoCheckFrequency: 15,
  autoCheckEnabled: true,

  discordLinked: false,
};
